import { useCallback, useEffect } from 'react';

import { type TJsonQuery, getOfferTypeFromJsonQuery, isSuburban } from '@/packages/JsonQuery';
import { type TOnSubmitSaveSearchParams, useSaveSearchContext } from '@/packages/SaveSearchModal';
import { type TContext } from '@/packages/SaveSearchModal/shared/types/context';
import { useFiltersSelector } from '@/packages/redux-state/store/slices/jsonQuery';

import {
  trackSaveSearchSubscribed,
  trackShowSaveSearchClick,
  trackSuburbanCloseSaveSearchClick,
  trackSuburbanSaveSearchSubscribed,
  trackSuburbanShowSaveSearchClick,
} from '../tracking';

const subscribeHandler = (
  isSuburbanSearch: boolean,
  jsonQuery: TJsonQuery,
  params: TOnSubmitSaveSearchParams,
): void => {
  const { defaultEmail, defaultTitle, subscribeToPushAvailable, ...commonParameters } = params;

  if (isSuburbanSearch) {
    trackSuburbanSaveSearchSubscribed({
      ...commonParameters,
      defaultTitle,
      defaultEmail,
      jsonQuery,
    });
  }

  trackSaveSearchSubscribed({
    ...commonParameters,
    jsonQuery,
    subscribeToPushAvailable,
  });
};

interface IUseSaveSearchModalReturnType {
  open: boolean;
  onSubscribed: (params: TOnSubmitSaveSearchParams) => void;
  onModalOpen: () => void;
  onModalClose: () => void;
  subscribeToPush: TContext['subscribeToPush'];
}

/**
 * Копипаста логики из SaveSearchButtonContainer без кнопки (кнопка лежит в самом микрофронте фильтров)
 * Открытие модалки происходит через событие микрофронта
 */
export const useSaveSearchModal = (): IUseSaveSearchModalReturnType => {
  const { open, onClose, onOpen, subscribeToPush } = useSaveSearchContext();

  const jsonQuery = useFiltersSelector();
  const isSuburbanSearch = isSuburban(getOfferTypeFromJsonQuery(jsonQuery));

  const onSubscribed = useCallback(
    (params: TOnSubmitSaveSearchParams) => {
      subscribeHandler(isSuburbanSearch, jsonQuery, params);
    },
    [isSuburbanSearch, jsonQuery],
  );

  const onModalOpen = useCallback(() => {
    trackShowSaveSearchClick();

    if (isSuburbanSearch) {
      trackSuburbanShowSaveSearchClick(jsonQuery);
    }
  }, [isSuburbanSearch, jsonQuery]);

  const onModalClose = useCallback(() => {
    onClose();

    if (isSuburbanSearch) {
      trackSuburbanCloseSaveSearchClick(jsonQuery);
    }
  }, [isSuburbanSearch, jsonQuery]);

  /**
   * Вот подписка
   * В дальнейшем SaveSearchButton будет вынесена в отдельный микрофронт и дубль логики уйдет
   */
  useEffect(() => {
    addEventListener('saveSearchButton:clicked', onOpen);

    return (): void => {
      removeEventListener('saveSearchButton:clicked', onOpen);
    };
  }, [onOpen]);

  return { open, onSubscribed, onModalOpen, onModalClose, subscribeToPush };
};
